import { ref } from 'vue'
import { db, storage } from '@/firebase/firebase'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  where
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { compressAndConvertToWebP, generateWebPFileName } from '@/utils/imageUtils'

/**
 * 매거진 관리 Composable
 * - 인스타그램 포스트 형태의 매거진 콘텐츠 관리
 * - Admin에서 등록, 메인 홈에서 노출
 */
export function useMagazine() {
  const magazines = ref([])
  const loading = ref(false)
  const error = ref(null)

  /**
   * 이미지 업로드 (WebP 변환)
   */
  const uploadImage = async (file) => {
    const webpBlob = await compressAndConvertToWebP(file)
    const fileName = generateWebPFileName(file.name)
    const fileRef = storageRef(storage, `magazines/${fileName}`)

    await uploadBytes(fileRef, webpBlob, { contentType: 'image/webp' })
    return await getDownloadURL(fileRef)
  }

  /**
   * 활성화된 매거진 목록 조회 (메인 홈용)
   * @param {number} limitCount - 가져올 개수 (기본 4개)
   */
  const getActiveMagazines = async (limitCount = 4) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'magazines'),
        where('isActive', '==', true),
        orderBy('order', 'asc')
      )
      const snapshot = await getDocs(q)

      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return items.slice(0, limitCount)
    } catch (err) {
      console.error('매거진 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 전체 매거진 목록 조회 (Admin용)
   */
  const getAllMagazines = async () => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'magazines'),
        orderBy('order', 'asc')
      )
      const snapshot = await getDocs(q)

      magazines.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return magazines.value
    } catch (err) {
      console.error('매거진 목록 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 매거진 등록
   * @param {Object} data - 매거진 데이터
   * @param {File} imageFile - 이미지 파일
   */
  const createMagazine = async (data, imageFile) => {
    loading.value = true
    error.value = null

    try {
      let imageUrl = ''
      if (imageFile) {
        imageUrl = await uploadImage(imageFile)
      }

      // 현재 최대 order 값 조회
      const allMagazines = await getAllMagazines()
      const maxOrder = allMagazines.length > 0
        ? Math.max(...allMagazines.map(m => m.order || 0))
        : 0

      const docRef = await addDoc(collection(db, 'magazines'), {
        title: data.title,
        linkUrl: data.linkUrl,
        imageUrl,
        date: data.date || new Date().toISOString().split('T')[0],
        order: maxOrder + 1,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      return { success: true, id: docRef.id }
    } catch (err) {
      console.error('매거진 등록 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  /**
   * 매거진 수정
   */
  const updateMagazine = async (id, data, imageFile = null) => {
    loading.value = true
    error.value = null

    try {
      const updateData = {
        title: data.title,
        linkUrl: data.linkUrl,
        date: data.date,
        isActive: data.isActive,
        updatedAt: new Date().toISOString()
      }

      if (imageFile) {
        updateData.imageUrl = await uploadImage(imageFile)
      }

      await updateDoc(doc(db, 'magazines', id), updateData)
      return { success: true }
    } catch (err) {
      console.error('매거진 수정 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  /**
   * 매거진 삭제
   */
  const deleteMagazine = async (id) => {
    loading.value = true
    error.value = null

    try {
      await deleteDoc(doc(db, 'magazines', id))
      magazines.value = magazines.value.filter(m => m.id !== id)
      return { success: true }
    } catch (err) {
      console.error('매거진 삭제 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  /**
   * 매거진 순서 변경
   */
  const updateOrder = async (id, newOrder) => {
    try {
      await updateDoc(doc(db, 'magazines', id), {
        order: newOrder,
        updatedAt: new Date().toISOString()
      })
      return { success: true }
    } catch (err) {
      console.error('순서 변경 실패:', err)
      return { success: false, error: err.message }
    }
  }

  /**
   * 매거진 활성화/비활성화 토글
   */
  const toggleActive = async (id, isActive) => {
    try {
      await updateDoc(doc(db, 'magazines', id), {
        isActive,
        updatedAt: new Date().toISOString()
      })

      // 로컬 상태 업데이트
      const magazine = magazines.value.find(m => m.id === id)
      if (magazine) {
        magazine.isActive = isActive
      }

      return { success: true }
    } catch (err) {
      console.error('상태 변경 실패:', err)
      return { success: false, error: err.message }
    }
  }

  return {
    magazines,
    loading,
    error,
    getActiveMagazines,
    getAllMagazines,
    createMagazine,
    updateMagazine,
    deleteMagazine,
    updateOrder,
    toggleActive
  }
}
